import {
  IBodyProductSearch,
  ICatalogState,
  TCatalogFormatView,
  TProductInfoFieldNameTypes,
  TQuerySearchArray,
  TSortBy,
} from 'interfaces/catalog.interface';
import * as CatalogTypes from './catalog.types';
import {
  IgetCatalogResponseData,
  IgetCatalogResponseDataItem,
  IProductState,
} from 'api/endpoints/catalog/catalog.response.interface';
import { IgetProductAttributesAPIResponseItem } from 'api/endpoints/general/general.response.interface';

export type TcatalogDispatchReducer =
  | {
      type: typeof CatalogTypes.GET_CATALOG;
      payload: IgetCatalogResponseData;
    }
  | {
      type: typeof CatalogTypes.ADD_MORE_TO_CATALOG;
      payload: IgetCatalogResponseData;
    }
  | {
      type: typeof CatalogTypes.GET_PRODUCT;
      payload: IProductState;
    }
  | {
      type: typeof CatalogTypes.UPDATE_PRODUCT;
      payload: {
        fieldName: TProductInfoFieldNameTypes;
        value: string;
      };
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.ADD_LIKE;
      payload: {
        productId: string;
        favorite: {
          favorite_id: string;
          user_id: string;
        };
      };
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING_ADD_LIKE;
      payload: {
        productId: string;
        isLoading: boolean;
      };
    }
  | {
      type: typeof CatalogTypes.REMOVE_LIKE;
      payload: {
        productId: string;
        favorite_id: string;
      };
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING_REMOVE_LIKE;
      payload: {
        productId: string;
        isLoading: boolean;
      };
    }
  | {
      type: typeof CatalogTypes.SET_BODY_PRODUCT;
      payload: {
        brandId: string;
        limit: number;
        languageId: string;
      };
    }
  | {
      type: typeof CatalogTypes.SET_BODY_PRODUCT_SEARCH;
      payload: IBodyProductSearch;
    }
  | {
      type: typeof CatalogTypes.SET_BODY_PRODUCT_SEARCH_TEMP;
      payload: IBodyProductSearch;
    }
  | {
      type: typeof CatalogTypes.SET_QUERY_SEARCH;
      payload: string;
    }
  | {
      type: typeof CatalogTypes.ADD_BODY_FILTERS;
      payload: {
        field: string;
        value: string;
      };
    }
  | {
      type: typeof CatalogTypes.REMOVE_BODY_FILTERS;
      payload: {
        field: string;
        value: string;
      };
    }
  | {
      type: typeof CatalogTypes.ADD_TO_QUERY_SEARCH_ARRAY;
      payload: TQuerySearchArray;
    }
  | {
      type: typeof CatalogTypes.REMOVE_FROM_QUERY_SEARCH_ARRAY;
      payload: TQuerySearchArray;
    }
  | {
      type: typeof CatalogTypes.SET_BODY_SEARCH_FILTER;
      payload: {
        querySearchArray: Array<TQuerySearchArray>;
      };
    }
  | {
      type: typeof CatalogTypes.RESET_BODY_FILTERS;
      payload?: null;
    }
  | {
      type: typeof CatalogTypes.INCREMENT_PAGINATION_INDEX;
      payload: number;
    }
  | {
      type: typeof CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.SET_IS_EMPTY_PRODUCTS;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.SET_SORT_BY;
      payload: TSortBy;
    }
  | {
      type: typeof CatalogTypes.SET_PRICE_SLIDERS;
      payload: {
        field: string;
        value: string;
      };
    }
  | {
      type: typeof CatalogTypes.SET_FAVORITES;
      payload: string | null;
    }
  | {
      type: typeof CatalogTypes.SET_RELATED_PRODUCTS;
      payload: {
        data: Array<IgetCatalogResponseDataItem>;
        count: number;
        active_seasons: number;
        published_catalogs: number;
        total: number;
        of: number;
      };
    }
  | {
      type: typeof CatalogTypes.SET_IS_LOADING_PRODUCT;
      payload: boolean;
    }
  | {
      type: typeof CatalogTypes.RESET_PRICE_SLIDER;
      payload: { type: string };
    }
  | {
      type: typeof CatalogTypes.RESET_CATALOG;
      payload?: null;
    }
  | {
      type: typeof CatalogTypes.SET_PRODUCT_ATTRIBUTES;
      payload: {
        season: Array<IgetProductAttributesAPIResponseItem>;
        division: Array<IgetProductAttributesAPIResponseItem>;
        gender: Array<IgetProductAttributesAPIResponseItem>;
        family: Array<IgetProductAttributesAPIResponseItem>;
        color: Array<IgetProductAttributesAPIResponseItem>;
        segmentation: Array<IgetProductAttributesAPIResponseItem>;
      };
    }
  | {
      type: typeof CatalogTypes.SET_FORMAT_VIEW;
      payload: TCatalogFormatView;
    };

export default function catalogReducer(
  state: ICatalogState,
  action: TcatalogDispatchReducer
) {
  const { type, payload } = action;

  switch (type) {
    case CatalogTypes.GET_CATALOG: {
      const getCatalog: ICatalogState = {
        ...state,
        catalogItems: payload,
      };
      return getCatalog;
    }

    case CatalogTypes.SET_RELATED_PRODUCTS: {
      const setRelatedProducts: ICatalogState = {
        ...state,
        relatedProducts: payload,
      };
      return setRelatedProducts;
    }

    case CatalogTypes.ADD_MORE_TO_CATALOG: {
      const addMoreToCatalog: ICatalogState = {
        ...state,
        catalogItems: {
          ...(state.catalogItems as IgetCatalogResponseData),
          data: (state.catalogItems as IgetCatalogResponseData).data?.concat(
            payload.data
          ),
        },
      };
      return addMoreToCatalog;
    }

    case CatalogTypes.GET_PRODUCT: {
      const getProduct: ICatalogState = {
        ...state,
        productItem: payload,
      };
      return getProduct;
    }

    case CatalogTypes.UPDATE_PRODUCT: {
      const updateProduct: ICatalogState = {
        ...state,
        productItem: {
          ...(state.productItem as IProductState),
          [payload.fieldName]: payload.value,
        },
      };
      return updateProduct;
    }

    case CatalogTypes.SET_IS_LOADING: {
      const setIsLoading: ICatalogState = {
        ...state,
        isLoading: payload,
      };
      return setIsLoading;
    }

    case CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS: {
      const setIsLoadingMoreProducts: ICatalogState = {
        ...state,
        isLoadingMoreProducts: payload,
      };
      return setIsLoadingMoreProducts;
    }

    case CatalogTypes.ADD_LIKE: {
      let addLikeState: ICatalogState = {
        ...state,
      };
      if (addLikeState.catalogItems) {
        addLikeState = {
          ...addLikeState,
          catalogItems: {
            ...(addLikeState.catalogItems as IgetCatalogResponseData),
            data: (addLikeState.catalogItems as IgetCatalogResponseData).data?.map(
              (item) => {
                if (item.id === payload.productId) {
                  return {
                    ...item,
                    favorite: [...item.favorite, payload.favorite],
                  };
                }
                return item;
              }
            ),
          },
        };
      }
      if (addLikeState.relatedProducts) {
        addLikeState = {
          ...addLikeState,
          relatedProducts: {
            ...(addLikeState.relatedProducts as IgetCatalogResponseData),
            data: (addLikeState.relatedProducts as IgetCatalogResponseData)?.data?.map(
              (item) => {
                if (item.id === payload.productId) {
                  return {
                    ...item,
                    favorite: [...item.favorite, payload.favorite],
                  };
                }
                return item;
              }
            ),
          },
        };
      }
      if (
        addLikeState.productItem &&
        addLikeState.productItem.product_id === payload.productId
      ) {
        addLikeState = {
          ...addLikeState,
          productItem: {
            ...(addLikeState.productItem as IProductState),
            favorite: [
              ...(addLikeState.productItem as IProductState).favorite,
              payload.favorite,
            ],
          },
        };
      }
      return addLikeState;
    }

    case CatalogTypes.SET_IS_LOADING_ADD_LIKE: {
      const setIsLoadingAddLike: ICatalogState = {
        ...state,
        isLoadingAddLike: {
          productId: payload.productId,
          isLoading: payload.isLoading,
        },
      };
      return setIsLoadingAddLike;
    }

    case CatalogTypes.REMOVE_LIKE: {
      let removeLikeState: ICatalogState = {
        ...state,
      };
      if (removeLikeState.catalogItems) {
        removeLikeState = {
          ...removeLikeState,
          catalogItems: {
            ...(removeLikeState.catalogItems as IgetCatalogResponseData),
            data: (removeLikeState.catalogItems as IgetCatalogResponseData).data?.map(
              (item) => {
                if (item.id === payload.productId) {
                  return {
                    ...item,
                    favorite: item.favorite.filter(
                      (favorite) => favorite.favorite_id !== payload.favorite_id
                    ),
                  };
                }
                return item;
              }
            ),
          },
        };
      }
      if (removeLikeState.relatedProducts) {
        removeLikeState = {
          ...removeLikeState,
          relatedProducts: {
            ...(removeLikeState.relatedProducts as IgetCatalogResponseData),
            data: (removeLikeState.relatedProducts as IgetCatalogResponseData).data?.map(
              (item) => {
                if (item.id === payload.productId) {
                  return {
                    ...item,
                    favorite: item.favorite.filter(
                      (favorite) => favorite.favorite_id !== payload.favorite_id
                    ),
                  };
                }
                return item;
              }
            ),
          },
        };
      }
      if (
        removeLikeState.productItem &&
        removeLikeState.productItem.product_id === payload.productId
      ) {
        removeLikeState = {
          ...removeLikeState,
          productItem: {
            ...(removeLikeState.productItem as IProductState),
            favorite: (removeLikeState.productItem as IProductState).favorite.filter(
              (favorite) => favorite.favorite_id !== payload.favorite_id
            ),
          },
        };
      }
      return removeLikeState;
    }

    case CatalogTypes.SET_IS_LOADING_REMOVE_LIKE: {
      const setIsLoadingRemoveLike: ICatalogState = {
        ...state,
        isLoadingRemoveLike: {
          productId: payload.productId,
          isLoading: payload.isLoading,
        },
      };
      return setIsLoadingRemoveLike;
    }

    case CatalogTypes.SET_BODY_PRODUCT: {
      const setBodyProduct: ICatalogState = {
        ...state,
        bodyProductSearch: {
          ...state.bodyProductSearch,
          options: {
            ...state.bodyProductSearch.options,
            brand_id: [payload.brandId],
            language_id: payload.languageId,
            limit: payload.limit,
            index: 0,
          },
          filter: {
            reference: [],
            color: [],
            season: [],
            segmentation: [],
            division: [],
            family: [],
            gender: [],
            pvi: [],
            pvpr: [],
            tags: [],
            tiers: [],
          },
        },
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          options: {
            ...state.bodyProductSearchTemp.options,
            brand_id: [payload.brandId],
            language_id: payload.languageId,
            limit: payload.limit,
            index: 0,
          },
          filter: {
            reference: [],
            color: [],
            season: [],
            segmentation: [],
            division: [],
            family: [],
            gender: [],
            pvi: [],
            pvpr: [],
            tags: [],
            tiers: [],
          },
        },
        querysearchArray: [],
      };
      return setBodyProduct;
    }

    case CatalogTypes.SET_BODY_PRODUCT_SEARCH: {
      const setBodyProductSearch: ICatalogState = {
        ...state,
        bodyProductSearch: payload,
      };
      return setBodyProductSearch;
    }

    case CatalogTypes.SET_BODY_PRODUCT_SEARCH_TEMP: {
      const setBodyProductSearchTemp: ICatalogState = {
        ...state,
        bodyProductSearchTemp: payload,
      };
      return setBodyProductSearchTemp;
    }

    case CatalogTypes.SET_QUERY_SEARCH: {
      const setQuerySearch: ICatalogState = {
        ...state,
        querySearch: payload,
      };
      return setQuerySearch;
    }

    case CatalogTypes.INCREMENT_PAGINATION_INDEX: {
      const incrementPaginationIndex: ICatalogState = {
        ...state,
        bodyProductSearch: {
          ...state.bodyProductSearch,
          options: {
            ...state.bodyProductSearch.options,
            index: payload,
          },
        },
      };
      return incrementPaginationIndex;
    }

    case CatalogTypes.SET_PRICE_SLIDERS: {
      const setPriceSliders: ICatalogState = {
        ...state,
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          filter: {
            ...state.bodyProductSearchTemp.filter,
            [payload.field]: payload.value,
          },
        },
      };
      return setPriceSliders;
    }

    case CatalogTypes.ADD_BODY_FILTERS: {
      const addBodyFilters: ICatalogState = {
        ...state,
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          filter: {
            ...state.bodyProductSearchTemp.filter,
            [payload.field]: [
              ...state.bodyProductSearchTemp.filter[
                payload.field as keyof typeof state.bodyProductSearchTemp.filter
              ],
              payload.value,
            ],
          },
        },
      };
      return addBodyFilters;
    }

    case CatalogTypes.REMOVE_BODY_FILTERS: {
      const removeBodyFilters: ICatalogState = {
        ...state,
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          filter: {
            ...state.bodyProductSearchTemp.filter,
            [payload.field]: (
              state.bodyProductSearchTemp.filter[
                payload.field as keyof typeof state.bodyProductSearchTemp.filter
              ] as string[]
            ).filter((value) => value !== payload.value),
          },
        },
      };
      return removeBodyFilters;
    }

    case CatalogTypes.RESET_BODY_FILTERS: {
      const resetBodyFilters: ICatalogState = {
        ...state,
        bodyProductSearch: {
          ...state.bodyProductSearch,
          options: {
            ...state.bodyProductSearch.options,
            favorite: '',
            // search: [""],
            index: 0,
          },
          filter: {
            reference: [],
            color: [],
            season: [],
            segmentation: [],
            division: [],
            family: [],
            gender: [],
            pvi: [],
            pvpr: [],
            tags: [],
            tiers: [],
          },
        },
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          options: {
            ...state.bodyProductSearchTemp.options,
            favorite: '',
            // search: [""],
            index: 0,
          },
          filter: {
            reference: [],
            color: [],
            season: [],
            segmentation: [],
            division: [],
            family: [],
            gender: [],
            pvi: [],
            pvpr: [],
            tags: [],
            tiers: [],
          },
        },
        querysearchArray: [],
      };
      return resetBodyFilters;
    }

    case CatalogTypes.RESET_PRICE_SLIDER: {
      const resetPriceSlider: ICatalogState = {
        ...state,
        bodyProductSearch: {
          ...state.bodyProductSearch,
          filter: {
            ...state.bodyProductSearch.filter,
            [payload.type]: [],
          },
        },
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          filter: {
            ...state.bodyProductSearchTemp.filter,
            [payload.type]: [],
          },
        },
      };
      return resetPriceSlider;
    }

    case CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS: {
      const setThereAreMoreProducts: ICatalogState = {
        ...state,
        thereAreMoreProducts: payload,
      };
      return setThereAreMoreProducts;
    }

    case CatalogTypes.SET_IS_EMPTY_PRODUCTS: {
      const setIsEmptyProducts: ICatalogState = {
        ...state,
        isEmptyProducts: payload,
      };
      return setIsEmptyProducts;
    }

    case CatalogTypes.SET_SORT_BY: {
      const setSortBy: ICatalogState = {
        ...state,
        bodyProductSearch: {
          ...state.bodyProductSearch,
          options: {
            ...state.bodyProductSearch.options,
            order: [payload],
          },
        },
      };
      return setSortBy;
    }

    case CatalogTypes.SET_FAVORITES: {
      const setFavorites: ICatalogState = {
        ...state,
        bodyProductSearchTemp: {
          ...state.bodyProductSearchTemp,
          options: {
            ...state.bodyProductSearchTemp.options,
            favorite: payload,
          },
        },
      };
      return setFavorites;
    }

    case CatalogTypes.SET_IS_LOADING_PRODUCT: {
      const setIsLoadingProduct: ICatalogState = {
        ...state,
        isLoadingProduct: payload,
      };
      return setIsLoadingProduct;
    }

    case CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS: {
      const setIsLoadingRelatedProducts: ICatalogState = {
        ...state,
        isLoadingRelatedProducts: payload,
      };
      return setIsLoadingRelatedProducts;
    }

    case CatalogTypes.ADD_TO_QUERY_SEARCH_ARRAY: {
      const addToQuerySearchArray: ICatalogState = {
        ...state,
        querysearchArray: !state.querysearchArray.includes(payload)
          ? [...state.querysearchArray, payload]
          : state.querysearchArray,
      };
      return addToQuerySearchArray;
    }

    case CatalogTypes.REMOVE_FROM_QUERY_SEARCH_ARRAY: {
      const removeFromQuerySearchArray: ICatalogState = {
        ...state,
        querysearchArray: state.querysearchArray.filter(
          (item: TQuerySearchArray) => item !== payload
        ),
      };
      return removeFromQuerySearchArray;
    }

    case CatalogTypes.SET_BODY_SEARCH_FILTER: {
      const setBodySearchFilter: ICatalogState = {
        ...state,
        querysearchArray: payload.querySearchArray,
      };
      return setBodySearchFilter;
    }

    case CatalogTypes.RESET_CATALOG: {
      const resetCatalogItems: ICatalogState = {
        ...state,
        catalogItems: {
          count: 0,
          data: [],
          active_seasons: 0,
          published_catalogs: 0,
          total: 0,
          of: 0,
        },
        relatedProducts: {
          count: 0,
          data: [],
          active_seasons: 0,
          published_catalogs: 0,
          total: 0,
          of: 0,
        },
        querySearch: '',
        querysearchArray: [],
        productItem: null,
      };
      return resetCatalogItems;
    }

    case CatalogTypes.SET_PRODUCT_ATTRIBUTES: {
      const setProductAttributes: ICatalogState = {
        ...state,
        productAttributes: payload,
      };
      return setProductAttributes;
    }

    case CatalogTypes.SET_FORMAT_VIEW: {
      const setFormatView: ICatalogState = {
        ...state,
        formatView: payload,
      };
      return setFormatView;
    }

    default:
      return state;
  }
}
